import React, {useState, useEffect, useMemo, useContext} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Download01Icon, EyeIcon, FilterIcon, GitCompareIcon, Refresh01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {FilterDrawer} from "./components/FilterDrawer";
import {AnnotationRow} from "./components/AnnotationRow";
import {ExportDialog} from "./components/ExportDialog";
import LoadingProgress from "../../../components/loading-screen/LoadingProgress";
import {MessageAnnotationsRepository} from "../../../repositories/MessageAnnotationRepository";
import {MobileUnavailable} from "../../../components/MobileUnavailable";


export const MessageAnnotationsPage = ({project}) => {
  // const {configuration, project} = useContext(AppContext);
  const configuration = project?.configuration || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pagination, setPagination] = useState({page: 0, size: 10, totalElements: 0});
  const [statistics, setStatistics] = useState({});
  const [filters, setFilters] = useState({
    messageId: null,
    conversationId: null,
    annotatedText: null,
    startDate: null,
    endDate: null,
    resolved: null,
    showOnlyUnresolved: false,
  });
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [viewMode, setViewMode] = useState('normal'); // 'normal' or 'diff'

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.messageId) count++;
    if (filters.conversationId) count++;
    if (filters.annotatedText) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.showOnlyUnresolved) count++;
    return count;
  }, [filters]);

  // Calculate resolved percentage
  const resolvedPercentage = useMemo(() => {
    const total = statistics?.totalAnnotations;
    const resolved = statistics?.totalResolved;
    if (total && resolved !== undefined && resolved !== null && total !== 0) {
      return ((resolved / total) * 100).toFixed(1);
    }
    return null;
  }, [statistics]);

  useEffect(() => {
    fetchAnnotations();
    fetchStatistics();
  }, [pagination.page, pagination.size]);

  const fetchStatistics = async () => {
    try {
      const response = await MessageAnnotationsRepository.getStatistics({voiceAssistantId: project?.id});
      setStatistics(response.data || {});
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    }
  };

  const fetchAnnotations = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        voiceAssistantId: project?.id,
        page: pagination.page,
        size: pagination.size,
        sort: "dateCreated",
        direction: "DESC",
      };

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await MessageAnnotationsRepository.search(params);
      setAnnotations(response.data.content || []);
      setPagination((prev) => ({
        ...prev,
        totalElements: response.data.totalElements || 0,
      }));
    } catch (error) {
      console.error("Failed to fetch annotations:", error);
      toast.error("Failed to fetch annotations");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({...prev, page: 0}));
    fetchAnnotations();
    fetchStatistics();
    setDrawerOpen(false);
  };

  const handleRefresh = () => {
    fetchAnnotations();
    fetchStatistics();
  };

  const handleClearFilters = () => {
    setFilters({
      messageId: null,
      conversationId: null,
      annotatedText: null,
      startDate: null,
      endDate: null,
      resolved: null,
      showOnlyUnresolved: false,
    });
  };

  // Update annotation in state (when editing or toggling resolved)
  const handleUpdateAnnotation = (updatedAnnotation) => {
    setAnnotations((prev) =>
      prev.map((ann) => (ann.id === updatedAnnotation.id ? updatedAnnotation : ann))
    );
    fetchStatistics();
  };

  // Delete annotation and refetch
  const handleDeleteAnnotation = () => {
    fetchAnnotations();
    fetchStatistics();
  };

  if (isMobile) {
    return <MobileUnavailable/>;
  }

  return (
    <Box maxWidth={"xl"} sx={{height: "100%", width: "90%", mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: 2}}>
      {loading && <LoadingProgress/>}
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
        <Typography variant="h5" fontWeight={600}>
          Message Annotations
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* View Mode Toggle */}
          <Stack
            gap={0} direction={"row"}
          >
            <Button
              onClick={() => setViewMode("normal")}
              color={"secondary"}
              variant={viewMode === "normal" ? "contained" : "outlined"}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                py: 0.5, px: 2,
                border: viewMode !== "normal" ? "1px solid !important" : 0, borderColor: "secondary.main !important",
                boxShadow: 0,
                // color: 'secondary.main',
                borderRadius: 0,
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
                display: "flex", justifyContent: "center",
                flexDirection: "row", gap: 1
              }}
              startIcon={<HugeiconsIcon icon={EyeIcon} size={16}/>}
            >
              Normal

            </Button>
            <Button
              onClick={() => setViewMode("diff")}
              color={"secondary"}
              variant={viewMode === "diff" ? "contained" : "outlined"}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                py: 0.5, px: 2,
                border: viewMode !== "diff" ? "1px solid !important" : 0, borderColor: "secondary.main !important",
                boxShadow: 0,
                // color: 'secondary.main',
                borderRadius: 0,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                display: "flex", justifyContent: "center",
                flexDirection: "row", gap: 1
              }}
              startIcon={<HugeiconsIcon icon={GitCompareIcon} size={16}/>}
            >
              Diff
            </Button>
          </Stack>

          {/* Export Button */}
          <Button
            variant="ghost"
            onClick={() => setExportDialogOpen(true)}
            sx={{
              fontSize: 12, py: 0.5, fontWeight: 600,
            }}
            startIcon={<HugeiconsIcon icon={Download01Icon} size={16}/>}
          >
            Export CSV
          </Button>
          {/* Refresh Button */}
          <IconButton
            variant="ghost"
            color={"secondary"}
            size={"small"}
            onClick={handleRefresh}
            sx={{boxShadow: 0}}
          >
              <HugeiconsIcon icon={Refresh01Icon} size={18}/>
          </IconButton>
          {/* Filter Button */}
          <IconButton
            variant="ghost"
            color={"secondary"}
            size={"small"}
            onClick={() => setDrawerOpen(true)}
            sx={{boxShadow: 0}}
          >
            <Badge badgeContent={activeFiltersCount} color="primary">
              <HugeiconsIcon icon={FilterIcon} size={18}/>
            </Badge>
          </IconButton>

        </Stack>
      </Stack>

      <Grid container spacing={2} justifyContent={"space-around"} sx={{bgcolor: 'grey.100', p: 2, borderRadius: 4}}>
        <Grid item sx={{textAlign: "center", p: 2}}>
          <Typography variant={"h3"} gutterBottom fontWeight={600}>{statistics?.totalAnnotations || 0}</Typography>
          <Typography variant={"body2"}>Total</Typography>
        </Grid>
        <Grid item sx={{textAlign: "center", p: 2}}>
          <Typography variant={"h3"} gutterBottom fontWeight={600}>
            {resolvedPercentage || "0.00"}%
          </Typography>
          <Typography variant={"body2"}>Resolved</Typography>
        </Grid>
        <Grid item sx={{textAlign: "center", p: 2}}>
          <Typography variant={"h3"} gutterBottom fontWeight={600}>{statistics?.avgCerScore?.toFixed(2) || "0.00"}</Typography>
          <Typography variant={"body2"}>CER</Typography>
        </Grid>
        <Grid item sx={{textAlign: "center", p: 2}}>
          <Typography variant={"h3"} gutterBottom fontWeight={600}>{statistics?.avgWerScore?.toFixed(2) || "0.00"}</Typography>
          <Typography variant={"body2"}>WER</Typography>
        </Grid>
      </Grid>

      {/* Filter Drawer */}
      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />

      {/* Table Container */}

      <Box sx={{flex: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
        <Box sx={{flex: 1, overflowY: "auto"}}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 700, fontSize: 12}}>Source Text</TableCell>
                <TableCell sx={{fontWeight: 700, fontSize: 12}}>Annotated Text</TableCell>
                <TableCell padding={"checkbox"} sx={{fontWeight: 700, fontSize: 12, px: 2}}>CER/WER</TableCell>
                <TableCell padding={"checkbox"} sx={{fontWeight: 700, fontSize: 12}}>Audio</TableCell>
                <TableCell padding={"checkbox"} sx={{fontWeight: 700, fontSize: 12}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {annotations.map((annotation) => (
                <AnnotationRow
                  key={annotation.id}
                  annotation={annotation}
                  selectedAudio={selectedAudio}
                  setSelectedAudio={setSelectedAudio}
                  configuration={configuration}
                  onUpdate={handleUpdateAnnotation}
                  onDelete={handleDeleteAnnotation}
                  viewMode={viewMode}
                />
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.totalElements}
          page={pagination.page}
          onPageChange={(e, page) => setPagination((prev) => ({...prev, page}))}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={(e) =>
            setPagination((prev) => ({...prev, page: 0, size: +e.target.value}))
          }
        />
      </Box>
    </Box>
  );
};
